import type React from "react";
import { useState, MouseEvent } from "react";

import {
  ExpandMore,
  ExpandLess,
  Close,
  Business,
  Logout,
  Person,
} from "@mui/icons-material";

import {
  Container,
  Header,
  CloseButton,
  Avatar,
  AvatarImage,
  AvatarFallback,
  UserInfo,
  UserDetails,
  UserName,
  Content,
  Section,
  SectionLabel,
  PortalButton,
  PortalButtonContent,
  PortalButtonText,
  ExpandableContent,
  ExpandableNavList,
  ExpandableNavItem,
  NavItemIcon,
  NavItemText,
  LoginHeader,
  LoginTitle,
} from "./style";
import { LoginButtonProps } from "../LoginButton/LoginButton";
import { SignInGovBrButtonWhite } from "../SignInGovBrButton/SignInGovBrButtonWhite";
import { AccountLevel } from "../AccountLevel/AccountLevel";

enum AccountLevelEnum {
  BRONZE = "bronze",
  PRATA = "prata",
  OURO = "ouro",
}

interface UserProfile {
  name: string;
  nivel?: AccountLevelEnum;
  photoPath?: string;
  isPhotoFromExternalApi?: boolean;
}

interface NavLink {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface UserProfileDropdownProps {
  user?: UserProfile;
  portalNavLinks: NavLink[];
  citizenNavLinks?: NavLink[];
  onClose?: () => void;
  onNavigate?: (path: string) => void;
  onLogout?: () => void;
  loginProps?: LoginButtonProps;
  onNavigateCatalog: (menuItemPath: string, mouseEvent: MouseEvent) => void;
  onNavigatePanel: (menuItemPath: string, mouseEvent: MouseEvent) => void;
}

export const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  user,
  portalNavLinks,
  citizenNavLinks,
  onClose,
  onNavigateCatalog,
  onNavigatePanel,
  onLogout,
  loginProps,
}) => {
  const [isPortalExpanded, setIsPortalExpanded] = useState(false);
  const [isCitizenExpanded, setIsCitizenExpanded] = useState(false);

  const hasUser = user && user.name && user.name.trim() !== "";

  return (
    <Container>
      <Header>
        <CloseButton onClick={onClose}>
          <Close fontSize="small" />
        </CloseButton>

        {hasUser ? (
          <UserInfo>
            <Avatar>
              {user.photoPath ? (
                <AvatarImage
                  src={user.photoPath || "/placeholder.svg"}
                  alt={user.name}
                />
              ) : (
                <AvatarFallback>
                  {user.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              )}
            </Avatar>

            <UserDetails>
              <UserName>{user.name}</UserName>
              <AccountLevel level={Number(user.nivel)} />
            </UserDetails>
          </UserInfo>
        ) : (
          <LoginHeader>
            <LoginTitle>Entrar</LoginTitle>
            <SignInGovBrButtonWhite onClick={loginProps?.onLogin} />
          </LoginHeader>
        )}
      </Header>

      <Content>
        <Section>
          <SectionLabel>Portal Estadual:</SectionLabel>

          <div>
            <PortalButton
              onClick={() => setIsPortalExpanded(!isPortalExpanded)}
            >
              <PortalButtonContent>
                <Business
                  fontSize="small"
                  style={{ color: "var(--color-grey-70)" }}
                />
                <PortalButtonText>Portal</PortalButtonText>
              </PortalButtonContent>
              {isPortalExpanded ? (
                <ExpandLess
                  fontSize="small"
                  style={{ color: "var(--color-grey-70)" }}
                />
              ) : (
                <ExpandMore
                  fontSize="small"
                  style={{ color: "var(--color-grey-70)" }}
                />
              )}
            </PortalButton>

            <ExpandableContent isExpanded={isPortalExpanded}>
              {portalNavLinks.length > 0 && (
                <ExpandableNavList>
                  {portalNavLinks.map((link, index) => (
                    <ExpandableNavItem
                      key={index}
                      href={`/app/catalog${link?.path}`}
                      onClick={(e: MouseEvent) =>
                        onNavigateCatalog(link?.path, e)
                      }
                    >
                      {link?.icon && <NavItemIcon>{link.icon}</NavItemIcon>}
                      {link?.label && <NavItemText>{link.label}</NavItemText>}
                    </ExpandableNavItem>
                  ))}
                </ExpandableNavList>
              )}
            </ExpandableContent>
          </div>
        </Section>

        {hasUser && !!citizenNavLinks?.length && (
          <Section>
            <SectionLabel>Painel do Cidadão:</SectionLabel>

            <div>
              <PortalButton
                onClick={() => setIsCitizenExpanded(!isCitizenExpanded)}
              >
                <PortalButtonContent>
                  <Person
                    fontSize="small"
                    style={{ color: "var(--color-grey-70)" }}
                  />
                  <PortalButtonText>Painel do Cidadão</PortalButtonText>
                </PortalButtonContent>
                {isCitizenExpanded ? (
                  <ExpandLess
                    fontSize="small"
                    style={{ color: "var(--color-grey-70)" }}
                  />
                ) : (
                  <ExpandMore
                    fontSize="small"
                    style={{ color: "var(--color-grey-70)" }}
                  />
                )}
              </PortalButton>

              <ExpandableContent isExpanded={isCitizenExpanded}>
                {citizenNavLinks.length > 0 && (
                  <ExpandableNavList>
                    {citizenNavLinks.map((link, index) => (
                      <ExpandableNavItem
                        key={index}
                        href={`/app/painel-cidadao${link?.path}`}
                      >
                        <NavItemIcon>{link.icon}</NavItemIcon>
                        <NavItemText>{link.label}</NavItemText>
                      </ExpandableNavItem>
                    ))}
                  </ExpandableNavList>
                )}
              </ExpandableContent>
            </div>
          </Section>
        )}

        {hasUser && (
          <Section>
            <ExpandableNavItem onClick={onLogout}>
              <NavItemIcon>
                <Logout fontSize="small" />
              </NavItemIcon>
              <NavItemText>Sair</NavItemText>
            </ExpandableNavItem>
          </Section>
        )}
      </Content>
    </Container>
  );
};
